import { PDFDocument } from 'pdf-lib';
import { DEFAULT_TOOL_SETTINGS } from '../constants';
import { makeText } from './objects';
import { measuredTextHeight, ensureEditorFonts } from './drawObjects';
import { paperPx, resolveMargins, docFontStack } from '../../../utils/aiDocExport';

/**
 * Open an AI-generated BLOCK / HTML document in the shared PDF editor.
 *
 * The AI document is HTML flow (blocks or one html string); the editor's model is
 * geometric text boxes in PDF points. This flattens the document — honouring the
 * current page/font/margin settings — into a blank PDF (one page per sheet) plus the
 * editable text objects laid on it, mirroring engine/layoutToPdf.js. The preview is
 * px @96dpi and the editor is points, so everything is scaled by PX_TO_PT (uniform),
 * which keeps the editor page 1:1 with the on-screen preview.
 *
 * Round-trip: after editing, serializeEditorLayout() turns the editor's objects back
 * into a { kind:'objects', pages, objects } layout that is persisted on the document
 * (as a `layout` block) and reopened losslessly via objectLayoutToPdf().
 */

const PX_TO_PT = 0.75; // 72/96 — CSS px @96dpi → PDF points
const DOC_FONT_BOOST = 1.15; // generated docs open a touch larger in the editor

// Per-kind defaults mirror aiDocExport KIND_FONT_PX / the preview's block styles.
const KIND = {
  title:   { px: 27,   bold: true,  align: 'center', color: '#111111', gap: 10 },
  meta:    { px: 11,   bold: false, align: 'center', color: '#6B6B6B', gap: 14 },
  heading: { px: 12.5, bold: true,  align: 'left',   color: '#111111', gap: 4 },
  para:    { px: 15.5, bold: false, align: 'left',   color: '#1C1815', gap: 8 },
  sig:     { px: 15,   bold: false, align: 'left',   color: '#1C1815', gap: 8 },
};
const LINE_H = 1.45;

/**
 * The exact font stack the preview (aiDocExport.buildPrintHtml) renders a kind with, so
 * the editor resolves the same face per glyph — for a Bangla doc that means Kalpurush,
 * because the Latin families (Georgia/Arial/mono) carry no Bangla and fall through to the
 * appended `bn` fallback. A chosen document font (globalFont) wins on body text.
 */
function fontFor(kind, globalFont, isBn) {
  const bn = isBn ? ",'Kalpurush','Noto Serif Bengali'" : '';
  const globalStack = docFontStack(globalFont); // null when "default font"
  let stack;
  if (kind === 'meta') stack = `'SF Mono',Menlo,monospace${bn}`;
  else if (kind === 'heading') stack = `Arial,Helvetica,sans-serif${bn}`;
  else stack = `${globalStack || "Georgia,'Times New Roman',serif"}${bn}`;
  const family = stack.split(',')[0].replace(/['"]/g, '').trim();
  return { stack, family };
}

/** Every family named across the given font stacks (for loading before we measure). */
function familiesIn(fonts) {
  const set = new Set();
  for (const f of fonts) for (const part of (f?.stack || '').split(',')) {
    const name = part.replace(/['"]/g, '').trim();
    if (name && !/^(serif|sans-serif|monospace)$/i.test(name)) set.add(name);
  }
  return [...set];
}

/** Load each family (regular + bold) so canvas measureText/paint use it, not a fallback. */
async function loadFonts(fonts) {
  if (!document.fonts?.load) return;
  await Promise.all(familiesIn(fonts).flatMap((fam) => [
    document.fonts.load(`400 16px "${fam}"`).catch(() => {}),
    document.fonts.load(`700 16px "${fam}"`).catch(() => {}),
  ]));
}

/** Plain text of an inline-HTML snippet (keeps [placeholders] and text, drops b/i/u tags). */
function stripInline(html) {
  const el = document.createElement('div');
  el.innerHTML = String(html ?? '');
  return (el.textContent || '').replace(/ /g, ' ');
}

/** A text object for one styled line, sized by the width it will have in the editor (pt). */
function lineObject(ln, { left, widthPt }) {
  return makeText(
    { x: left, y: 0, w: widthPt, h: Math.max(5, ln.px * PX_TO_PT) * LINE_H },
    {
      ...DEFAULT_TOOL_SETTINGS.text,
      ...(ln.font ? { fontFamily: ln.font.family, fontStack: ln.font.stack } : {}),
      fontSize: Math.max(5, ln.px * PX_TO_PT),
      color: ln.color,
      bold: ln.bold,
      align: ln.align,
      lineHeight: LINE_H,
    },
    ln.text,
  );
}

/* ---------------------------------------------------------------- normalize */

/** Block document → flat list of styled lines. */
function blocksToLines(blocks, fontScale, fontOf) {
  const out = [];
  for (const b of blocks || []) {
    if (!b || typeof b.text !== 'string') continue;
    const kind = KIND[b.kind] ? b.kind : 'para';
    const base = KIND[kind];
    const text = stripInline(b.text);
    // Blank para = paragraph gap.
    if (!text.trim()) { out.push({ blank: true, px: base.px }); continue; }
    const align = ['left', 'center', 'right', 'justify'].includes(b.align) ? b.align : base.align;
    const px = (typeof b.fontSize === 'number' && b.fontSize > 0 ? b.fontSize : base.px) * (fontScale || 1);
    out.push({ text, px, bold: base.bold, align, color: base.color, gap: base.gap, font: fontOf(kind) });
  }
  return out;
}

/** HTML document → flat list of styled lines (best-effort flatten of block elements). */
function htmlToLines(html, fontScale, fontOf) {
  const out = [];
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const sizeOf = (tag) => {
    if (tag === 'h1') return KIND.title.px;
    if (tag === 'h2' || tag === 'h3') return KIND.heading.px + 2;
    if (tag === 'h4' || tag === 'h5' || tag === 'h6') return KIND.heading.px;
    return KIND.para.px;
  };
  const push = (text, kind, { px, bold = false, align = 'left', color = KIND.para.color, gap = KIND.para.gap } = {}) => {
    const t = (text || '').replace(/\s+/g, ' ').trim();
    if (!t) return;
    out.push({ text: t, px: px * (fontScale || 1), bold, align, color, gap, font: fontOf(kind) });
  };
  const walk = (node) => {
    for (const el of node.children) {
      const tag = el.tagName.toLowerCase();
      if (/^h[1-6]$/.test(tag)) {
        const kind = tag === 'h1' ? 'title' : 'heading';
        push(el.textContent, kind, { px: sizeOf(tag), bold: true, align: tag === 'h1' ? 'center' : 'left', gap: 6 });
      } else if (tag === 'p' || tag === 'div' || tag === 'li') {
        push(el.textContent, 'para', { px: KIND.para.px });
      } else if (tag === 'table') {
        for (const tr of el.querySelectorAll('tr')) {
          const cells = [...tr.children].map((c) => (c.textContent || '').replace(/\s+/g, ' ').trim());
          push(cells.join('      '), 'para', { px: KIND.para.px, gap: 2 });
        }
        out.push({ blank: true, px: KIND.para.px });
      } else if (el.children.length) {
        walk(el);
      } else {
        push(el.textContent, 'para', { px: KIND.para.px });
      }
    }
  };
  walk(doc.body);
  return out;
}

/* -------------------------------------------------------------------- flow */

/**
 * Flow styled lines down page(s), all in PDF points. Each box's height comes from the
 * editor's OWN `measuredTextHeight` (same canvas wrap + line-height + TEXT_PAD it paints
 * with), so a box is exactly as tall as the text the editor draws into it — no overlap,
 * no spurious page breaks. → { pages:[{w,h} pt], objects:[makeText + .page] }.
 */
async function flowToObjects(lines, { wPx, hPx, marginsPx }) {
  const left = marginsPx.left * PX_TO_PT;
  const top = marginsPx.top * PX_TO_PT;
  const bottom = marginsPx.bottom * PX_TO_PT;
  const pageW = wPx * PX_TO_PT;
  const pageH = hPx * PX_TO_PT;
  const widthPt = pageW - left - marginsPx.right * PX_TO_PT;

  // Build every text object up front, then warm the fonts the editor measures with —
  // measuredTextHeight relies on canvas measureText, which is wrong until they load. Load
  // BOTH the catalogue fonts the editor knows AND the document's own stacks (Kalpurush etc).
  const seq = lines.map((ln) =>
    ln.blank ? { gap: ln.px * PX_TO_PT * LINE_H * 0.55 }
             : { obj: lineObject(ln, { left, widthPt }), gap: (ln.gap || 0) * PX_TO_PT });
  const objs = seq.filter((s) => s.obj).map((s) => s.obj);
  await Promise.all([
    ensureEditorFonts(objs),
    loadFonts(lines.filter((ln) => ln.font).map((ln) => ln.font)),
  ]);

  const pages = [{ w: pageW, h: pageH }];
  const objects = [];
  let page = 0;
  let y = top;

  for (const s of seq) {
    if (!s.obj) { y += s.gap; continue; }
    const h = measuredTextHeight(s.obj);
    if (y + h > pageH - bottom && objects.some((o) => o.page === page)) {
      page += 1;
      pages.push({ w: pageW, h: pageH });
      y = top;
    }
    s.obj.x = left;
    s.obj.y = y;
    s.obj.h = h;
    s.obj.page = page;
    objects.push(s.obj);
    y += h + s.gap;
  }
  return { pages, objects };
}

/** A blank multi-page PDF of the given point sizes, as a File. */
async function blankPdf(pages, name) {
  const doc = await PDFDocument.create();
  pages.forEach((p) => doc.addPage([p.w, p.h]));
  const bytes = await doc.save();
  const safe = String(name || 'document').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'document';
  return new File([bytes], `${safe}.pdf`, { type: 'application/pdf' });
}

/* ------------------------------------------------------------------ public */

/**
 * First open: a block/html document + its page settings → { file, objects, layout }.
 * @param {{ blocks?:object[], html?:string, name?:string, settings?:object }} args
 *   settings: { paper, landscape, globalFontScale, margins }
 */
export async function docToObjects({ blocks = null, html = null, name = 'document', settings = {} } = {}) {
  const { paper = 'a4', landscape = false, globalFontScale = 1, margins = null, globalFont = null, isBn = true } = settings;
  const { w: wPx, h: hPx } = paperPx(paper, landscape);
  const m = resolveMargins(margins, { landscape });

  // Nudge the whole document a little larger in the editor — the on-screen preview
  // reads bigger than the points the editor lays out, so lift it ~15% for parity.
  const scale = (globalFontScale || 1) * DOC_FONT_BOOST;
  const fontOf = (kind) => fontFor(kind, globalFont, isBn);
  const lines = html != null
    ? htmlToLines(html, scale, fontOf)
    : blocksToLines(blocks, scale, fontOf);
  const { pages, objects } = await flowToObjects(lines, { wPx, hPx, marginsPx: m });
  const file = await blankPdf(pages, name);
  return { file, objects, layout: { kind: 'objects', pages, objects } };
}

/** A blank one-page PDF File at the given paper size — a placeholder to open the editor
 *  on immediately while the real document is still being generated. */
export async function blankDocFile(settings = {}, name = 'document') {
  const { paper = 'a4', landscape = false } = settings;
  const { w: wPx, h: hPx } = paperPx(paper, landscape);
  return blankPdf([{ w: wPx * PX_TO_PT, h: hPx * PX_TO_PT }], name);
}

/** Reopen: a persisted { kind:'objects', pages, objects } layout → { file, objects }. */
export async function objectLayoutToPdf(layout, name = 'document') {
  const pages = Array.isArray(layout?.pages) && layout.pages.length ? layout.pages : [{ w: 595.28, h: 841.89 }];
  const objects = Array.isArray(layout?.objects) ? layout.objects : [];
  const file = await blankPdf(pages, name);
  return { file, objects };
}

/**
 * After editing: the editor's live store (pages + objectsByUid) → a persistable
 * { kind:'objects', pages, objects } layout. `objects` carry a 0-based `page` index.
 */
export function serializeEditorLayout(pages, objectsByUid) {
  const outPages = [];
  const outObjects = [];
  (pages || []).forEach((pg, idx) => {
    outPages.push({ w: pg.width, h: pg.height });
    for (const obj of objectsByUid?.[pg.uid] || []) {
      outObjects.push({ ...obj, page: idx });
    }
  });
  return { kind: 'objects', pages: outPages, objects: outObjects };
}
